import { useEffect, useState } from 'react';
import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { Loader2Icon } from 'lucide-react';
import { ChatMarkdown } from '@/components/assistant-ui/markdown-text';
import { useCentrifugeProgress } from '@/hooks/useCentrifugeProgress';
import {
  useCancelMcpSkillExecutionMutation,
  useGetMcpSkillExecutionQuery
} from '../../api/mcpApi';

type SkillRunResult = {
  status?: string;
  isDone?: boolean;
  message?: string;
};

function parseResult(result: unknown): SkillRunResult | null {
  if (!result) return null;
  if (typeof result === 'object' && !Array.isArray(result)) {
    return result as SkillRunResult;
  }
  if (typeof result !== 'string' || !result.trim()) return null;
  try {
    return JSON.parse(result) as SkillRunResult;
  } catch {
    return null;
  }
}

function executionIdFromArgs(args: unknown): string {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return '';
  const id = (args as { executionId?: unknown }).executionId;
  return typeof id === 'string' ? id.trim() : '';
}

/** Live progress for a skill that runs outside the chat request. */
export const SkillExecutionToolUI: ToolCallMessagePartComponent = ({
  args,
  result
}) => {
  const executionId = executionIdFromArgs(args);
  const stored = parseResult(result);
  const alreadyDone = stored?.isDone === true;
  const [sawConnection, setSawConnection] = useState(false);
  const [cancelExecution, { isLoading: cancelling }] =
    useCancelMcpSkillExecutionMutation();

  const { data, refetch } = useGetMcpSkillExecutionQuery(executionId, {
    skip: !executionId || alreadyDone
  });
  const done = alreadyDone || data?.isDone === true;

  const { connected, progressData } = useCentrifugeProgress({
    channelId: executionId,
    active: Boolean(executionId) && !done,
    onComplete: () => {
      void refetch();
    }
  });

  useEffect(() => {
    if (connected) setSawConnection(true);
  }, [connected]);

  useEffect(() => {
    if (!executionId || done) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [done, executionId, refetch]);

  const analysis =
    data?.status === 'succeeded' ? data.resultContent?.trim() : '';

  if (!executionId || alreadyDone) return null;
  if (analysis) return <ChatMarkdown text={analysis} />;

  const liveMessage =
    (typeof progressData?.message === 'string' && progressData.message.trim()) ||
    data?.message ||
    stored?.message ||
    'در صف اجرا';
  const failed = data?.status === 'failed' || data?.status === 'cancelled';
  const percent =
    typeof progressData?.progress_percentage === 'number'
      ? Math.min(100, Math.max(0, Math.round(progressData.progress_percentage)))
      : null;

  return (
    <div
      dir="rtl"
      className="border-border/70 bg-[var(--card)] text-foreground flex w-full flex-col gap-2.5 rounded-xl border px-3.5 py-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {!done ? (
            <Loader2Icon
              className="text-primary size-4 shrink-0 animate-spin"
              aria-hidden
            />
          ) : null}
          <span className="text-foreground text-sm leading-6 font-semibold">
            {done
              ? failed
                ? 'اجرای مهارت تمام نشد'
                : 'مهارت تمام شد'
              : liveMessage}
          </span>
        </div>
        {!done ? (
          <button
            type="button"
            className="mcp-chat-pill-btn mcp-chat-pill-btn--ghost mcp-chat-pill-btn--sm ms-auto shrink-0"
            disabled={cancelling}
            onClick={() => {
              void cancelExecution(executionId)
                .unwrap()
                .then(() => refetch());
            }}
          >
            {cancelling ? 'در حال لغو…' : 'لغو'}
          </button>
        ) : null}
      </div>
      {!done ? (
        <div
          className="bg-primary/15 relative h-1 overflow-hidden rounded-full"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent ?? undefined}
          aria-label="پیشرفت اجرای مهارت"
          style={{ direction: 'ltr' }}
        >
          <span
            className={
              percent == null
                ? 'bg-primary mcp-skill-run__bar--indeterminate block h-full rounded-full'
                : 'bg-primary block h-full rounded-full transition-[width] duration-300'
            }
            style={percent == null ? undefined : { width: `${percent}%` }}
          />
        </div>
      ) : null}
      {!done && sawConnection && !connected ? (
        <p className="text-muted-foreground mb-0 text-xs leading-6">
          نمایش زنده قطع شد؛ اجرای مهارت ادامه دارد.
        </p>
      ) : null}
      {data?.error && failed ? (
        <p className="text-destructive mb-0 text-xs leading-6">{data.error}</p>
      ) : null}
    </div>
  );
};
